import { DateTime, IANAZone } from "luxon";

export const CALENDAR_SELECTED_INSTANT_KEY = "jobCreateSelectedDateExact";

export type CompanyWallTimeResolution =
  | { kind: "valid"; choices: Array<{ instant: string; label: string }> }
  | { kind: "ambiguous"; choices: Array<{ instant: string; label: string }> }
  | { kind: "nonexistent"; choices: []; suggestedTime: string };

export const resolveCompanyWallTime = (date: Date, timeZone: string): CompanyWallTimeResolution => {
  if (!IANAZone.isValidZone(timeZone) || Number.isNaN(date.getTime())) {
    throw new Error("Invalid company time zone or date");
  }

  const wall = {
    year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(),
    hour: date.getHours(), minute: date.getMinutes(), second: date.getSeconds(),
    millisecond: date.getMilliseconds(),
  };
  const resolved = DateTime.fromObject(wall, { zone: timeZone });
  const matchesWall = (candidate: DateTime) =>
    candidate.year === wall.year && candidate.month === wall.month && candidate.day === wall.day &&
    candidate.hour === wall.hour && candidate.minute === wall.minute &&
    candidate.second === wall.second && candidate.millisecond === wall.millisecond;

  if (!resolved.isValid || !matchesWall(resolved)) {
    return { kind: "nonexistent", choices: [], suggestedTime: resolved.isValid ? resolved.toFormat("h:mm a") : "" };
  }

  const choices = resolved.getPossibleOffsets()
    .filter(matchesWall)
    .sort((a, b) => a.toMillis() - b.toMillis())
    .map(candidate => ({ instant: candidate.toUTC().toISO()!, label: candidate.toFormat("h:mm a ZZZZ") }));

  return { kind: choices.length > 1 ? "ambiguous" : "valid", choices };
};

export const formatCompanyInstant = (instant: string, timeZone: string, format = "h:mm a"): string => {
  const local = DateTime.fromISO(instant, { setZone: true }).setZone(timeZone);
  if (!local.isValid) return "";
  const repeated = local.getPossibleOffsets().length > 1;
  return local.toFormat(repeated ? `${format} ZZZZ` : format);
};

export const isRepeatedCompanyInstant = (instant: string, timeZone: string): boolean => {
  const local = DateTime.fromISO(instant, { setZone: true }).setZone(timeZone);
  return local.isValid && local.getPossibleOffsets().length > 1;
};
